import type {
  BlockingEvent,
  BlockingMapSnapshot,
  DataScope,
  EventHazard,
  ExplorerCatalog,
} from "../types/explorer";

export const SCOPE_LABEL: Record<DataScope, string> = {
  "validated-historical": "Validated thesis · 1979–2018",
  "extended-2026": "New 2026 · never used in training",
};

export const SCOPE_HINT: Record<DataScope, string> = {
  "validated-historical":
    "Thesis-era ERA5. Expert GTD labels exist for these dates.",
  "extended-2026":
    "Post-training ERA5T (after 2020). Same weights; no GTD labels yet.",
};

export function eventTitle(event: {
  id: string;
  title?: string;
  playGroup?: string;
}): string {
  return event.title ?? event.playGroup ?? event.id;
}

export function eventAnchorId(event: Pick<BlockingEvent, "id">): string {
  return `event-${event.id}`;
}

export const HAZARD_LABEL: Record<EventHazard, string> = {
  heatwave: "Heatwave",
  drought: "Drought / wildfire",
  wildfire: "Wildfire",
  flood: "Flood",
  "cold-spell": "Cold spell",
  other: "Other",
};

export const HAZARD_OPTIONS: EventHazard[] = [
  "heatwave",
  "drought",
  "flood",
  "cold-spell",
  "other",
];

export function eventHazard(event: BlockingEvent): EventHazard {
  if (event.hazard) return event.hazard;
  if (event.hazards?.length) return event.hazards[0];
  const key = `${event.playGroup ?? ""} ${event.id} ${event.notes ?? ""}`.toLowerCase();
  if (key.includes("flood")) return "flood";
  if (key.includes("cold")) return "cold-spell";
  if (key.includes("drought") || key.includes("wildfire") || key.includes("fire")) {
    return "drought";
  }
  if (key.includes("quiet") || key.includes("contrast")) return "other";
  if (key.includes("heat")) return "heatwave";
  return "other";
}

export function eventHazards(event: BlockingEvent): EventHazard[] {
  if (event.hazards?.length) return [...new Set(event.hazards)];
  return [eventHazard(event)];
}

export async function fetchCatalog(): Promise<ExplorerCatalog> {
  const res = await fetch("/data/index.json", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Could not load explorer catalog (${res.status})`);
  }
  return res.json();
}

export async function fetchEvents(): Promise<BlockingEvent[]> {
  const res = await fetch("/data/events.json", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Could not load events (${res.status})`);
  }
  return res.json();
}

export async function fetchMap(date: string): Promise<BlockingMapSnapshot> {
  const res = await fetch(`/data/maps/${date}.json`, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`No map for ${date}`);
  }
  return res.json();
}

export const DEFAULT_VALID_TIME = "00:00 UTC";

export function formatIsoDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function snapshotValidTime(
  snapshot?: Pick<BlockingMapSnapshot, "meta"> | null,
): string {
  return snapshot?.meta.validTime ?? DEFAULT_VALID_TIME;
}

export function formatSnapshotStamp(
  date: string,
  validTime: string = DEFAULT_VALID_TIME,
): string {
  return `${formatIsoDate(date)}, ${validTime}`;
}

export function eventCoversDate(event: BlockingEvent, date: string): boolean {
  return date >= event.startDate && date <= event.endDate;
}

export type IndexName = "TM90" | "DG83" | "S04";

export interface IndexFlags {
  GTD: boolean | null;
  TM90: boolean | null;
  DG83: boolean | null;
  S04: boolean | null;
}

export const INDEX_LABEL: Record<IndexName, string> = {
  TM90: "TM90 · Z500 gradient",
  DG83: "DG83 · Z500 anomaly",
  S04: "S04 · PV anomaly",
};

export function indexFlagsForEvent(event: BlockingEvent | undefined): IndexFlags {
  if (!event || event.scope === "extended-2026") {
    return { GTD: null, TM90: null, DG83: null, S04: null };
  }
  if (event.cnnOnly) {
    return { GTD: false, TM90: false, DG83: false, S04: false };
  }
  const sources = new Set(event.sources);
  if (sources.size === 0) {
    return { GTD: false, TM90: false, DG83: false, S04: false };
  }
  const hasGtd = sources.has("GTD");
  return {
    GTD: hasGtd,
    TM90: sources.has("TM90") ? true : hasGtd ? null : false,
    DG83: sources.has("DG83") ? true : hasGtd ? null : false,
    S04: sources.has("S04") ? true : hasGtd ? null : false,
  };
}

export function indexAverage(flags: IndexFlags): number | null {
  const votes = [flags.TM90, flags.DG83, flags.S04];
  if (votes.some((vote) => vote === null)) return null;
  return votes.filter(Boolean).length / 3;
}
